import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FileText, AlertTriangle, Sparkles, Calendar, Plus, Users, X, Clock, FileBox, Lightbulb, CheckSquare, ChevronRight, CheckCircle2 } from 'lucide-react';
import { ProposalHeaderData, RiskSignal } from '../lib/types';
const statusStyles = {
  'at-risk': {
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300',
    label: 'At Risk',
    icon: AlertTriangle
  },
  'needs-attention': {
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300',
    label: 'Needs Attention',
    icon: AlertTriangle
  },
  'on-track': {
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300',
    label: 'On Track',
    icon: CheckCircle2
  }
};
const iconMap: Record<string, React.ElementType> = {
  Clock,
  FileBox,
  Lightbulb,
  CheckSquare
};
interface ProposalHeaderProps {
  data: ProposalHeaderData;
  artifactsCompleted?: number;
  artifactsTotal?: number;
}
export function ProposalHeader({
  data,
  artifactsCompleted = 0,
  artifactsTotal = 0
}: ProposalHeaderProps) {
  const [showRiskPopover, setShowRiskPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowRiskPopover(false);
      }
    }
    if (showRiskPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRiskPopover]);
  const riskStyle = statusStyles[data.riskStatus];
  const RiskIcon = riskStyle.icon;
  return <Card className="border-0 shadow-sm" data-id="element-391">
      <CardContent className="p-5 sm:p-6" data-id="element-392">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5" data-id="element-393">
          {/* Left: Title + metadata */}
          <div className="flex-1 min-w-0" data-id="element-394">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5" data-id="element-395">
              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 border-0 text-[11px] font-medium px-2 h-5" data-id="element-396">
                {data.funder}
              </Badge>
              <Badge variant="outline" className="text-[11px] font-medium px-2 h-5" data-id="element-397">
                {data.mechanism}
              </Badge>
              <Badge className={`${data.proposalStatus === 'On Track' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'} border-0 text-[11px] font-medium px-2 h-5`} data-id="element-398">
                {data.proposalStatus}
              </Badge>
            </div>
            <h1 className="text-lg font-bold text-foreground leading-snug mb-1.5" data-id="element-399">
              {data.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground" data-id="element-400">
              <span className="inline-flex items-center gap-1.5" data-id="element-401">
                <FileText className="h-3.5 w-3.5" data-id="element-402" />
                {data.foaNumber}
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-403">
                {data.piName}
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-404">
                <Users className="h-3.5 w-3.5 flex-shrink-0" data-id="element-405" />
                <span className="flex items-center -space-x-1.5" data-id="element-406">
                  {data.team.map(person => <span key={person.initials} className="relative group/avatar" data-id="element-407">
                      <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold ring-2 ring-white cursor-default ${person.color}`} data-id="element-408">
                        {person.initials}
                      </span>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-md bg-foreground text-primary-foreground text-[11px] whitespace-nowrap opacity-0 pointer-events-none group-hover/avatar:opacity-100 transition-opacity shadow-lg z-50" data-id="element-409">
                        <span className="font-semibold block leading-tight" data-id="element-410">
                          {person.name}
                        </span>
                        <span className="text-primary-foreground/70 block leading-tight" data-id="element-411">
                          {person.institution}
                        </span>
                        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" data-id="element-412" />
                      </span>
                    </span>)}
                  <button title="Add collaborator" className="inline-flex items-center justify-center h-6 w-6 rounded-full border-[1.5px] border-dashed border-muted-foreground/40 text-muted-foreground/60 hover:border-muted-foreground hover:text-muted-foreground ring-2 ring-white bg-white transition-colors" data-id="element-413">
                    <Plus className="h-3 w-3" data-id="element-414" />
                  </button>
                </span>
              </span>
            </div>
          </div>

          {/* Right: Status + progress + deadline */}
          <div className="flex-shrink-0 w-full lg:w-56 flex flex-col lg:items-end gap-2.5" data-id="element-415">
            {/* Risk Badge — Clickable */}
            <div className="relative" ref={popoverRef} data-id="element-416">
              <button onClick={() => setShowRiskPopover(!showRiskPopover)} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-417">
                <Badge className={`${riskStyle.badge} text-xs font-bold px-3 py-1.5 h-auto gap-1.5 shadow-sm w-fit cursor-pointer`} data-id="element-418">
                  <RiskIcon className="h-4 w-4" data-id="element-419" />
                  {riskStyle.label}
                  <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[9px] px-1.5 py-0 h-4 gap-0.5 shadow-none ml-0.5 font-semibold rounded-full" data-id="element-420">
                    <Sparkles className="h-2.5 w-2.5" data-id="element-421" />
                    AI
                  </Badge>
                </Badge>
              </button>

              {/* Risk Assessment Popover */}
              {showRiskPopover && <div className="absolute top-full right-0 mt-2 z-50 w-[420px] rounded-lg bg-white border shadow-xl" data-id="element-422">
                  {/* Popover Header */}
                  <div className="flex items-center justify-between px-4 pt-3.5 pb-3 border-b border-border" data-id="element-423">
                    <div className="flex items-center gap-2" data-id="element-424">
                      <div className={`p-1.5 rounded-md border ${data.riskStatus === 'at-risk' ? 'bg-red-50 border-red-100' : data.riskStatus === 'needs-attention' ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`} data-id="element-425">
                        <RiskIcon className={`h-4 w-4 ${data.riskStatus === 'at-risk' ? 'text-red-600' : data.riskStatus === 'needs-attention' ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-426" />
                      </div>
                      <span className="font-semibold text-foreground text-sm" data-id="element-427">
                        Risk Assessment
                      </span>
                      <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium" data-id="element-428">
                        <Sparkles className="h-3 w-3" data-id="element-429" />
                        AI Analysis
                      </Badge>
                    </div>
                    <button onClick={() => setShowRiskPopover(false)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-id="element-430">
                      <X className="h-4 w-4" data-id="element-431" />
                    </button>
                  </div>

                  {/* AI Summary */}
                  <div className={`px-4 py-3 border-b border-border ${data.riskStatus === 'at-risk' ? 'bg-red-50/30' : data.riskStatus === 'needs-attention' ? 'bg-amber-50/30' : 'bg-emerald-50/30'}`} data-id="element-432">
                    <p className="text-xs text-foreground leading-relaxed italic" data-id="element-433">
                      {data.riskSummary}
                    </p>
                  </div>

                  {/* Signal Rows */}
                  {data.riskSignals.length > 0 && <div className="divide-y divide-border" data-id="element-434">
                      {data.riskSignals.map(signal => {
                  const SignalIcon = iconMap[signal.iconName] || AlertTriangle;
                  const style = statusStyles[signal.status];
                  return <button key={signal.id} className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left group" onClick={() => setShowRiskPopover(false)} data-id="element-435">
                            {/* Status dot */}
                            <div className={`w-2 h-2 rounded-full ${style.dot} mt-1.5 flex-shrink-0`} data-id="element-436" />

                            {/* Icon */}
                            <div className="p-1 rounded bg-muted/50 flex-shrink-0" data-id="element-437">
                              <SignalIcon className="h-3.5 w-3.5 text-muted-foreground" data-id="element-438" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0" data-id="element-439">
                              <div className="flex items-center gap-2 mb-0.5" data-id="element-440">
                                <span className="text-xs font-semibold text-foreground" data-id="element-441">
                                  {signal.label}
                                </span>
                                <Badge className={`${style.badge} text-[10px] px-1.5 py-0 h-4 shadow-none font-medium`} data-id="element-442">
                                  {style.label}
                                </Badge>
                              </div>
                              <p className="text-[11px] text-foreground leading-snug" data-id="element-443">
                                {signal.summary}
                              </p>
                              <p className="text-[11px] text-muted-foreground leading-snug mt-0.5" data-id="element-444">
                                {signal.detail}
                              </p>
                            </div>

                            {/* Arrow */}
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-muted-foreground mt-1.5 flex-shrink-0 transition-colors" data-id="element-445" />
                          </button>;
                })}
                    </div>}

                  {/* Footer */}
                  <div className="px-4 py-2.5 border-t border-border bg-muted/20 rounded-b-lg" data-id="element-446">
                    <p className="text-[10px] text-muted-foreground text-center" data-id="element-447">
                      Click a category to view details in the relevant tab
                    </p>
                  </div>
                </div>}
            </div>

            <div className="w-full" data-id="element-448">
              <div className="flex items-center justify-between mb-1" data-id="element-449">
                <span className="text-[11px] font-medium text-muted-foreground" data-id="element-450">
                  Completion
                </span>
                <span className="text-[11px] font-bold text-indigo-600" data-id="element-451">
                  {artifactsCompleted} / {artifactsTotal}
                </span>
              </div>
              <Progress value={artifactsTotal > 0 ? Math.round(artifactsCompleted / artifactsTotal * 100) : 0} className="h-1.5" data-id="element-452" />
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-id="element-453">
              <Calendar className="h-3 w-3 flex-shrink-0" data-id="element-454" />
              <span className="font-semibold text-foreground" data-id="element-455">
                {data.daysLeft} days left
              </span>
              <span className="text-muted-foreground" data-id="element-456">
                (Due {data.deadline})
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}